import React, { useEffect, useRef } from 'react';

/**
 * Single-viewport lesson step card: header (shrink), main (flex, clipped), optional dev overflow warn.
 *
 * @param {object} props
 * @param {string} [props.stepId]
 * @param {boolean} [props.tapFirst]
 * @param {boolean} [props.mainScrollable] — when true, main column scrolls (e.g. interactive steps + floating hint so taps are not clipped). When false, the header is not height-capped (recap/intro stay readable; narration may live in the shell footer on narrow).
 * @param {React.ReactNode} props.header
 * @param {React.ReactNode} props.main
 */
export default function LessonActivityCard({ stepId = '', tapFirst = false, mainScrollable = false, header, main }) {
  const rootRef = useRef(null);

  useEffect(() => {
    if (!import.meta.env.DEV || !rootRef.current) return undefined;
    const el = rootRef.current;
    const ro = new ResizeObserver(() => {
      const { clientHeight, scrollHeight, clientWidth, scrollWidth } = el;
      if (scrollHeight > clientHeight + 2 || scrollWidth > clientWidth + 2) {
        // eslint-disable-next-line no-console
        console.warn(
          `[LessonActivityCard] Content overflow for step "${stepId}" (${scrollWidth}×${scrollHeight} vs ${clientWidth}×${clientHeight}). Shorten copy or tighten engine layout.`,
        );
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [stepId]);

  return (
    <div
      ref={rootRef}
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          flexShrink: 0,
          minWidth: 0,
          ...(tapFirst && mainScrollable
            ? {
                maxHeight: 'min(30vh, 220px)',
                overflow: 'hidden',
              }
            : {}),
        }}
      >
        {header}
      </div>
      <div
        className={mainScrollable ? 'hide-scrollbar' : undefined}
        style={{
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: mainScrollable ? 'auto' : 'hidden',
          WebkitOverflowScrolling: mainScrollable ? 'touch' : undefined,
          overscrollBehaviorY: mainScrollable ? 'contain' : undefined,
          touchAction: mainScrollable ? 'pan-y' : undefined,
        }}
      >
        {main}
      </div>
    </div>
  );
}
